import React, { useState, useEffect } from 'react'
import { Box, Paper, Text, Flex, CopyButton, Tooltip, ThemeIcon, Group, Button, Skeleton, NumberInput, SimpleGrid, Textarea } from '@mantine/core'
import { IconCheck, IconCopy, IconWindowMaximize } from '@tabler/icons-react'
import { useSelector, useDispatch } from 'react-redux'
import { validation } from '../../../../../plugins/validation'
import { updateOrder, generateShortUrlOrder } from '../../../../../services/orders'
import { updateOrderSummary } from '../../../../../store/orderDetail'
import { notificationSuccess, notificationError } from '../../../../ui/Notifications'

const defaultVal = {
  so_duration_days: null,
  sales_note: ''
}

const formValidation = {
  so_duration_days: {
    isError: false,
    message: ''
  },
}

const FormSalesOrder = ({ idOrder, onCloseModal }) => {
  const dispatch = useDispatch()
  const { summary } = useSelector(state => state.order_detail)
  const [loadingShortLink, setLoadingShortLink] = useState(false)
  const [loadingForm, setLoadingForm] = useState(false)
  const [shortLinkURL, setShortLinkURL] = useState(null)
  const [formData, setFormData] = useState(defaultVal)
  const [validationForm, setValidationForm] = useState(formValidation)

  const submitUpdateOrder = async (form) => {
    setLoadingForm(true)
    setValidationForm(formValidation)
    const validateForm = {
      so_duration_days: `${form.so_duration_days}`,
    }
    const isError = validation(validateForm, setValidationForm)
    if (isError) {
      setLoadingForm(false)
      return
    }
    try {
      const response = await updateOrder(idOrder, form)
      if (response.success) {
        onCloseModal()
        const updatedSummary = {
          ...summary,
          soDurationDays: response.data.attributes.so_duration_days,
          salesNote: response.data.attributes.sales_note
        }
        dispatch(updateOrderSummary(updatedSummary))
        notificationSuccess('Update Order Successfully', 'This data has been updated')
      }
    } catch (error) {
      console.log(error)
      const errorMessage = error.response.data.message
      notificationError('Update Order Error', `${errorMessage ? errorMessage : 'Please recheck your form'}`)
    } finally {
      setLoadingForm(false)
    }
  }

  const generateShortUrl = async (orderId) => {
    setLoadingShortLink(true)
    try {
      const payload = { original_url: summary.linkSo }
      const response = await generateShortUrlOrder(orderId, payload)
      if (response.success) {
        const shortUrl = response.data.short_url
        setShortLinkURL(shortUrl)
        const updatedSummary = {
          ...summary,
          shortUrl
        }
        dispatch(updateOrderSummary(updatedSummary))
      }
    } catch (error) {
      console.log(error)
      notificationError('Can not generate short link', error.message)
    } finally {
      setLoadingShortLink(false)
    }
  }

  useEffect(() => {
    handleSetState(summary)
  }, [summary])

  const handleSetState = (value) => {
    console.log(value)
    setShortLinkURL(value.shortUrl)
    const detail = {
      so_duration_days: value.soDurationDays,
      sales_note: value.salesNote
    }
    setFormData(detail)
  }

  const handleChangeForm = (name, val) => {
    setFormData((oldVal) => ({ ...oldVal, [name]: val}))
  }

  const openNewTab = (url) => {
    window.open(`${url}`, '_blank')
  }

  const mappingShortLinkUrl = (dataUrl) => {
    if (dataUrl === null) {
      return (
        <Box ta='center'>
          <Button size='compact-xs' variant='transparent' color='blue.9' onClick={() => generateShortUrl(idOrder)}>Click to Generate Short Link</Button>
        </Box>
      )
    } else {
      return (
        <Flex gap={4} align='center'>
          <Text fz={12} c='blue.9'>{`${dataUrl}`}</Text>
          <CopyButton value={`${dataUrl}`} timeout={2000}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="top">
                <ThemeIcon color={copied ? 'teal' : 'blue.9'} variant="subtle" onClick={copy} size='xs'>
                  {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                </ThemeIcon>
              </Tooltip>
            )}
          </CopyButton>
          <Tooltip label='Open Link' withArrow position="top">
            <ThemeIcon color='blue.9' variant="subtle" size='xs' onClick={() => openNewTab(`${dataUrl}`)}>
              <IconWindowMaximize size={12} />
            </ThemeIcon>
          </Tooltip>
        </Flex>
      )
    }
  }


  return (
    <Box>
      <Box>
        <Paper shadow="sm" withBorder p="sm" mb='lg'>
          <Text fz={14} fw='600' mb={12}>Sales Order / quote URL for User</Text>
          <Box mb='xs'>
            <Text fz={12} fw='500' c='gray.6' mb={1}>EN URL:</Text>
            <Flex gap={4} align='center'>
              <Text fz={12} c='blue.9' truncate="end">{`${summary.linkSo}`}</Text>
              <CopyButton value={`${summary.linkSo}`} timeout={2000}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="top">
                    <ThemeIcon color={copied ? 'teal' : 'blue.9'} variant="subtle" onClick={copy} size='xs'>
                      {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                    </ThemeIcon>
                  </Tooltip>
                )}
              </CopyButton>
              <Tooltip label='Open Link' withArrow position="top">
                <ThemeIcon color='blue.9' variant="subtle" size='xs' onClick={() => openNewTab(`${summary.linkSo}`)}>
                  <IconWindowMaximize size={12} />
                </ThemeIcon>
              </Tooltip>
            </Flex>
          </Box>

          <Box mb='md'>
            <Text fz={12} fw='500' c='gray.6' mb={1}>Short URL:</Text>
            {loadingShortLink ? <Box w={100}><Skeleton height={14} radius="xl" /></Box> : mappingShortLinkUrl(shortLinkURL)}
          </Box>
        </Paper>

        <Paper shadow="sm" withBorder p="sm" mb='lg'>
          <SimpleGrid cols={2} verticalSpacing="sm" mb='sm'>
            <Box>
              <NumberInput
                label='Sales Order Validity'          
                value={formData.so_duration_days}
                rightSection={<Text fz={12} c='black'>Days from order date</Text>}
                onChange={(val) => handleChangeForm(`so_duration_days`, val)}
                error={validationForm.so_duration_days.isError ? `${validationForm.so_duration_days.message}` : ''}
                size='xs'
                rightSectionWidth={150}
                withAsterisk
              />
            </Box>
          </SimpleGrid>
          <Box mb='md'>
            <Textarea
              name='sales_note'
              label="Sales Order / Quote Note"
              value={formData.sales_note}
              onChange={(val) => handleChangeForm(`sales_note`, val.target.value)}
              size='xs'
              autosize
              minRows={6}
              maxRows={6}
            />
          </Box>
          <Box mb='xl'>
            <Button size='xs' loading={loadingForm} variant="filled" onClick={() => submitUpdateOrder(formData)}>Save</Button>
          </Box>
        </Paper>
      </Box>
      <Box mt={20}>
        <Flex justify='flex-end'>
          <Group>
            <Button size='xs' variant="outline" onClick={onCloseModal}>Close</Button>
          </Group>
        </Flex>
      </Box>
    </Box>
  )
}

export default FormSalesOrder